import React, { useState, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import Button from "../../components/Button";
import BaseLayout from "../../components/Layout";
import { actionGetAlertasAbertos } from "../../features/alertas/slices";
import { useAppDispatch } from "../../hooks/useAppDispatch";
import { useAppSelector } from "../../hooks/useAppSelector";
import ModalInserirInformacao from "./ModalInserirInformacao";
import ModalPlanoDeAcao from "./ModalPlanoDeAcao";
import ModalReconhecer from "./ModalReconhecer";
import "./styles.scss";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  Filter,
  Search,
  ShieldAlert,
  Trash2,
  X,
} from "lucide-react";
import { actionGetAlertaPlanoDeAcao } from "../../features/alertas/plano-de-acao/get-plano-acao/slices";
import { getUser } from "../../helpers/user";
import { actionGetAlertasUsuariosEncaminhados } from "../../features/alertas/get-encaminhado/slices";
import handleDownloadPDF from "../../features/alertas/alertaToPdf";
import { actionExcluirAlerta } from "../../features/alertas/delete-alerta/slices";
import { toast } from "sonner";
import { actionSelectLoja } from "../../features/dados-loja/lojas/slices";

const PageAlertas = () => {
  const dispatch = useAppDispatch();
  const user = getUser();
  const { idLoja } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();


  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);
  const [openModalReconhecer, setOpenModalReconhecer] = useState(null);
  const [openModalPlanoDeAcao, setOpenModalPlanoDeAcao] = useState(null);
  const [openModalInserirInfo, setOpenModalInserirInfo] = useState(null);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterOrder, setFilterOrder] = useState("");
  const [showSearchInput, setShowSearchInput] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itensPorPagina = 20;


  const getAlertasAbertos = useAppSelector((state) => state.getAlertasAbertos);
  const lojaSelected = useAppSelector((state) => state.getLojas.lojaSelected);
  const lojas = useAppSelector((state) => state.getLojas.lojas);
  const { loading: isExcluindo } = useAppSelector(
    (state) => state.excluirAlerta,
  );

  const idLojaUser =
    lojaSelected && lojaSelected !== "" ? Number(lojaSelected) : null;
  const idRegionalUser = user?.usuario?.idCliente as number | 0;

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus, filterOrder, lojaSelected]);


  const getAlertStatusConfig = (data: any) => {
    const agora = new Date();
    const dataNormalizacao = data.dataNormalizacao
      ? new Date(data.dataNormalizacao)
      : null;
    const dataPrevisao = data.dataPrevisaoPlanoAcao
      ? new Date(data.dataPrevisaoPlanoAcao)
      : null;

    const isNormalizado =
      dataNormalizacao !== null && dataNormalizacao.getTime() > 0;
    const prazoExcedido =
      data.planoAcaoAtivo &&
      !isNormalizado &&
      dataPrevisao &&
      dataPrevisao < agora;


    if (prazoExcedido) {
      return {
        label: "AVISO - PRAZO EXCEDIDO",
        colorClass: "status-vermelho",
        priority: 1,
      };
    }


    if (!data.reconhecido && !isNormalizado) {
      return {
        label: "AVISO - ALERTA ATUADO",
        colorClass: "status-vermelho",
        priority: 2,
      };
    }


    if (data.reconhecido || (data.planoAcaoAtivo && data.planoAcao !== "")) {
      if (!isNormalizado) {
        return {
          label: "ATENÇÃO - EM ANDAMENTO",
          colorClass: "status-laranja",
          priority: 3,
        };
      }
    }


    return { label: "RESOLVIDO", colorClass: "status-verde", priority: 4 };
  };


  const alertasOrdenados = useMemo(() => {
    if (!getAlertasAbertos?.data) return [];

    const nivelUsuario = user?.usuario?.nivel;

    return [...getAlertasAbertos.data]
      .map((item) => ({ ...item, config: getAlertStatusConfig(item) }))
      .sort((a, b) => {

        if (a.config.priority !== b.config.priority) {
          return a.config.priority - b.config.priority;
        }


        return (
          new Date(b.dataEvento).getTime() - new Date(a.dataEvento).getTime()
        );
      })
      .filter((item) => {
        const desc = item.descricao.toLowerCase();
        const isTecnico =
          desc.includes("encontra-se offline") ||
          desc.includes("módulo") ||
          desc.includes("central id:");

        if (isTecnico && nivelUsuario !== 999) {
          return false;
        }

        return true;
      });
  }, [getAlertasAbertos.data, user?.usuario?.nivel]);


  const alertasExibidos = useMemo(() => {
    let resultado = [...alertasOrdenados];

    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      resultado = resultado.filter(
        (item) =>
          item.id.toString().includes(search) ||
          item.descricao.toLowerCase().includes(search),
      );
    }

    if (filterStatus) {
      resultado = resultado.filter(
        (item) => item.config.label === filterStatus,
      );
    }


    if (filterOrder === "recentes") {
      resultado.sort(
        (a, b) =>
          new Date(b.dataEvento).getTime() - new Date(a.dataEvento).getTime(),
      );
    } else if (filterOrder === "antigos") {
      resultado.sort(
        (a, b) =>
          new Date(a.dataEvento).getTime() - new Date(b.dataEvento).getTime(),
      );
    }

    return resultado;
  }, [alertasOrdenados, searchTerm, filterStatus, filterOrder]);

  console.log("idLojaUser:", idLojaUser);

  useEffect(() => {
    const funcao = searchParams.get("funcao");
    const alertaId = searchParams.get("alertaId");
    const lojaIdViaUrl = searchParams.get("lojaId");



    if (lojaIdViaUrl && lojaSelected !== lojaIdViaUrl) {
      console.log("Setando loja via URL:", lojaIdViaUrl);
      dispatch(actionSelectLoja(lojaIdViaUrl));


      return;
    }


    if (getAlertasAbertos.status === "success" && getAlertasAbertos.data) {
      if (funcao === "abrirInformacao" && alertaId) {
        const alertaTarget = getAlertasAbertos.data.find(
          (item: any) => item.id === Number(alertaId),
        );

        if (alertaTarget) {
          setOpenModalInserirInfo(alertaTarget);
        } else {
          toast.error(`Alerta #${alertaId} não encontrado nesta loja.`);
        }


        const newParams = new URLSearchParams(searchParams);
        newParams.delete("funcao");
        newParams.delete("alertaId");
        newParams.delete("lojaId");
        setSearchParams(newParams, { replace: true });
      }
    }
  }, [
    searchParams,
    getAlertasAbertos.status,
    getAlertasAbertos.data,
    lojaSelected,
    dispatch,
    setSearchParams,
  ]);


  useEffect(() => {
    if (idLojaUser) {
      dispatch(
        actionGetAlertasAbertos({
          idLoja: idLojaUser,
          pagina: currentPage,
          itensPorPagina: itensPorPagina,
        }),
      );
      dispatch(
        actionGetAlertasUsuariosEncaminhados({
          idLoja: idLojaUser,
          idRegional: idRegionalUser,
          idCliente: user.usuario.idCliente,
        }),
      );
    }
  }, [
    idLojaUser,
    idRegionalUser,
    currentPage,
    dispatch,
    user.usuario.idCliente,
  ]);

  const handleExecutarExclusao = async (id: number, fisico: boolean) => {
    const result = await dispatch(actionExcluirAlerta({ id, fisico }));
    if (actionExcluirAlerta.fulfilled.match(result)) {
      setConfirmDeleteId(null);
      if (idLojaUser) dispatch(actionGetAlertasAbertos({ idLoja: idLojaUser }));
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };


  if (!lojaSelected && !getAlertasAbertos.data) {
    return (
      <BaseLayout>
        <div className="container">
          <strong>Para iniciar selecione uma loja</strong>
        </div>
      </BaseLayout>
    );
  }

  if (getAlertasAbertos.status === "loading") {
    return (
      <BaseLayout>
        <div className="container">
          <h3>Carregando...</h3>
        </div>
      </BaseLayout>
    );
  }

  const formatTimeAgo = (dateString: string) => {
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInSeconds = Math.floor(
      (now.getTime() - eventDate.getTime()) / 1000,
    );

    if (diffInSeconds < 60) return "agora mesmo";

    const diffInMinutes = Math.floor(diffInSeconds / 60);
    if (diffInMinutes < 60) return `há ${diffInMinutes} min`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24)
      return `há ${diffInHours} ${diffInHours === 1 ? "hora" : "horas"}`;

    const diffInDays = Math.floor(diffInHours / 24);
    return `há ${diffInDays} ${diffInDays === 1 ? "dia" : "dias"}`;
  };

  console.log("Alertas exibidos:", alertasExibidos);

  return (
    <BaseLayout>
      <div className="container">
        <div className="container" style={{ marginBottom: "1rem" }}>
          <strong style={{ fontSize: "2rem", fontWeight: 700 }}>
            {lojas.find((loja) => loja.id === Number(lojaSelected))?.nome ||
              "Alertas"}
          </strong>
        </div>

        <div className="filter-bar">
          <div className="search-box">
            <div
              onClick={() => setShowSearchInput(!showSearchInput)}
              className="search-trigger"
            >
              <Search size={18} /> <span>Buscar</span>
            </div>
            <input
              type="text"
              className={`search-input ${showSearchInput ? "visible" : ""}`}
              placeholder="ID ou descrição..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <div className="filter-label">
              <Filter size={16} /> <span>Filtrar:</span>
            </div>
            <div className="select-wrapper">
              <select
                title={""}
                value={filterOrder}
                onChange={(e) => setFilterOrder(e.target.value)}
              >
                <option value="">Prioridade de Status</option>
                <option value="recentes">Mais Recentes</option>
                <option value="antigos">Mais Antigos</option>
              </select>
              <select
                title={""}
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">Todos os Status</option>
                <option value="AVISO - PRAZO EXCEDIDO">Prazo Excedido</option>
                <option value="AVISO - ALERTA ATUADO">Alerta Atuado</option>
                <option value="ATENÇÃO - EM ANDAMENTO">Em Andamento</option>
                <option value="RESOLVIDO">Resolvido</option>
              </select>
            </div>
          </div>
        </div>

        <div className="alert-list">
          {alertasExibidos.map((data) => (
            <div
              key={data.id}
              className={`alert-list__item ${data.config.colorClass} ${confirmDeleteId === data.id ? "is-deleting" : ""}`}
            >
              <div className="header-row">
                <section className="status">
                  <div className="status-dot-wrapper">
                    <span
                      className="status-dot"
                      data-status={data.config.label}
                    ></span>
                  </div>
                  <div className="delete-wrapper">
                    {confirmDeleteId === data.id ? (
                      <div className="delete-options-popover">
                        <span className="popover-title">
                          Excluir Alerta #{data.id}?
                        </span>
                        <div className="popover-actions">
                          <button
                            className="btn-soft"
                            onClick={() =>
                              handleExecutarExclusao(data.id, false)
                            }
                            disabled={isExcluindo}
                          >
                            <ShieldAlert size={13} /> <span>Desativar</span>
                          </button>
                          <button
                            className="btn-hard"
                            onClick={() =>
                              handleExecutarExclusao(data.id, true)
                            }
                            disabled={isExcluindo}
                          >
                            <Trash2 size={13} /> <span>Apagar</span>
                          </button>
                          <button
                            title={""}
                            className="btn-cancel"
                            onClick={() => setConfirmDeleteId(null)}
                            disabled={isExcluindo}
                          >
                            <X size={13} />
                          </button>
                        </div>
                      </div>
                    ) : (
                      user.usuario.nivel === 999 && (
                        <button
                          className="btn-delete-trigger"
                          onClick={() => setConfirmDeleteId(data.id)}
                          title="Excluir alerta"
                        >
                          <X size={16} />
                        </button>
                      )
                    )}
                  </div>
                </section>
              </div>

              <section className="content-row">
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <span className="id-text">
                    Alerta #{data.id} - {formatTimeAgo(data.dataEvento)}
                  </span>
                  <ChevronDown
                    size={18}
                    className={`expand-icon ${expandedIds.includes(data.id) ? "active" : ""}`}
                    onClick={() => toggleExpanded(data.id)}
                  />
                </div>
                <p className="desc-text">{data.descricao}</p>
              </section>

              <nav>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setOpenModalInserirInfo(data)}
                >
                  Informar{" "}
                  <small className="info-count">{data.info.length}</small>
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    dispatch(actionGetAlertaPlanoDeAcao(data));
                    setOpenModalPlanoDeAcao(data);
                  }}
                >
                  Plano de Ação{" "}
                  {data.planoAcaoAtivo && (
                    <span className="planoAcao-ativo"></span>
                  )}
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={data.reconhecido}
                  onClick={() => setOpenModalReconhecer(data)}
                >
                  {data.reconhecido ? "Reconhecido" : "Reconhecer"}
                </Button>
                <Download
                  size={18}
                  className="download-icon"
                  onClick={() => handleDownloadPDF(data, user)}
                />
              </nav>

              <div
                className={`description__info ${expandedIds.includes(data.id) ? "expanded" : ""}`}
              >
                <div className="info-box">
                  <strong>Procedimento:</strong>
                  <p>
                    {data.procedimento || "Nenhum procedimento cadastrado."}
                  </p>
                </div>
                <div className="info-box">
                  <strong>Ocorreu em:</strong>
                  <p>{new Date(data.dataEvento).toLocaleString("pt-br")}</p>
                </div>
              </div>
            </div>
          ))}
          {alertasExibidos.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: "#888",
                fontFamily: "Arial",
              }}
            >
              Nenhum alerta crítico encontrado nesta página.
            </div>
          )}
        </div>
        <div
          className="pagination-container"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
            marginTop: "2rem",
            paddingBottom: "2rem",
          }}
        >
          <button
            className="btn-pagination"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "8px 12px",
              borderRadius: "6px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
              opacity: currentPage === 1 ? 0.5 : 1,
              border: "1px solid #ccc",
              background: "#fff",
            }}
          >
            <ChevronLeft size={16} /> Anterior
          </button>

          <span
            style={{ fontWeight: 600, fontSize: "1rem", fontFamily: "Arial" }}
          >
            Página {currentPage}
          </span>

          <button
            className="btn-pagination"
            onClick={() => setCurrentPage((prev) => prev + 1)}

            disabled={getAlertasAbertos?.data?.length < itensPorPagina}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              padding: "8px 12px",
              borderRadius: "6px",
              cursor:
                getAlertasAbertos?.data?.length < itensPorPagina
                  ? "not-allowed"
                  : "pointer",
              opacity:
                getAlertasAbertos?.data?.length < itensPorPagina ? 0.5 : 1,
              border: "1px solid #ccc",
              background: "#fff",
            }}
          >
            Próximo <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {openModalInserirInfo && (
        <ModalInserirInformacao
          data={openModalInserirInfo}
          close={() => setOpenModalInserirInfo(null)}
        />
      )}
      {openModalReconhecer && (
        <ModalReconhecer
          data={openModalReconhecer}
          close={() => setOpenModalReconhecer(null)}
        />
      )}
      {openModalPlanoDeAcao && (
        <ModalPlanoDeAcao
          data={openModalPlanoDeAcao}
          close={() => setOpenModalPlanoDeAcao(null)}
        />
      )}
    </BaseLayout>
  );
};

export default PageAlertas;
